import { MouseEventHandler, ButtonHTMLAttributes, ReactNode } from 'react'
import { motion, MotionProps } from 'framer-motion'
import { Loader } from '@components/v2/loader'
import { cn } from '@global/utils/tailwind'

const ButtonVariants = {
  dark: 'dark',
  outline: 'outline',
  primary: 'primary',
  secondary: 'secondary',
  select: 'select',
  tertiary: 'tertiary',
  text: 'text',
  warn: 'warn',
} as const

type ButtonProps = {
  onClick?: MouseEventHandler<HTMLButtonElement> | undefined
  isLoading?: boolean
  loadingText?: string
  children: ReactNode
  className?: string
  type?: 'button' | 'submit' | 'reset'
  size?: 'sm' | 'md' | 'lg'
  variant?: (typeof ButtonVariants)[keyof typeof ButtonVariants]
  innerElementLayout?: 'position' | 'size' | boolean | undefined
} & ButtonHTMLAttributes<HTMLButtonElement> &
  MotionProps

const animation = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.4 },
}

export function Button({
  id,
  onClick,
  isLoading = false,
  loadingText,
  children,
  type = 'button',
  className = '',
  variant = 'primary',
  size = 'md',
  disabled = false,
  layout = 'position',
  innerElementLayout = 'position',
  ...rest
}: ButtonProps) {
  const classes = cn({
    '!bg-cv-blue-100 !text-[#8E9EA5] !border-cv-blue-200 cursor-not-allowed': disabled || isLoading,
    'bg-cv-blue-600 border-cv-blue-600 text-white': variant === ButtonVariants.dark,
    'bg-cv-primary border-cv-primary text-white': variant === ButtonVariants.primary,
    'bg-cv-blue-150 border-cv-blue-300 text-cv-blue-600 font-medium':
      variant === ButtonVariants.secondary,
    'border-none text-cv-blue-600 font-medium': variant === ButtonVariants.text,
    'bg-cv-blue-150 border-cv-blue-400 text-cv-blue-700 font-medium':
      variant === ButtonVariants.tertiary,
    'bg-cv-white border-cv-blue-600': variant === ButtonVariants.outline,
    'bg-cv-blue-50 border-cv-blue-200 shadow': variant === ButtonVariants.select,
    'bg-cv-red-50 border-cv-red-200': variant === ButtonVariants.warn,
    'px-2 py-1 text-[12px]': size === 'sm',
    'px-4 py-2 text-sm': size === 'md',
    'px-6 py-3 text-md': size === 'lg',
  })

  return (
    <motion.button
      className={cn(
        'clvn-v2-button',
        'font-sans font-semibold text-sm text-nowrap',
        'transition-[filter,border-color,background-color,color] duration-200',
        'rounded-[4px] px-4 py-2 border hover:brightness-95',
        classes,
        className,
      )}
      onClick={onClick}
      key={`${id}-motion-key`}
      type={type}
      layout={layout}
      disabled={disabled}
      {...rest}
    >
      {isLoading ? (
        <motion.span
          className="flex gap-2 items-center clvn-v2-button__inner"
          {...animation}
          key="loading"
          layout={innerElementLayout}
        >
          <Loader />
          {loadingText || children}
        </motion.span>
      ) : (
        <motion.span
          className="flex gap-2 items-center clvn-v2-button__inner h-full"
          {...animation}
          key="children"
          layout={innerElementLayout}
        >
          {children}
        </motion.span>
      )}
    </motion.button>
  )
}
